import { lazy, memo, Suspense } from 'react';
import { TableHoverContent } from 'foundations-components/transitional/components/Table';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import I18n from 'I18n';
import { MilestoneSubjectCell, MilestoneStatusCheckbox, MilestoneTitleContent } from '../milestone/MilestoneTableCells/MilestoneSubjectCell';
import { MilestoneOwnerCell, MilestoneOwnerContent } from '../milestone/MilestoneTableCells/MilestoneOwnerCell';
import { MilestoneMenu } from '../milestone/MilestoneMenu';
import { MilestoneSuggestionActions } from '../milestone/MilestoneSuggestionActions';
import { StyledTableCell } from './StyledTableCell';
import { useMilestoneTableSize } from './MilestoneTableSizeContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LazyMilestoneDueDateCell = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "DEAL_PLAN_DATE_INPUT" */'../milestone/MilestoneTableCells/MilestoneDueDateCell').then(m => ({
  default: m.MilestoneDueDateCell
})));
const LazyMilestoneDateContent = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "DEAL_PLAN_DATE_INPUT" */'../milestone/MilestoneTableCells/MilestoneDueDateCell').then(m => ({
  default: m.MilestoneDateContent
})));

// Static date fallbacks shown while the DEAL_PLAN_DATE_INPUT chunk loads.
// Milestones with an existing date show the value as plain text to prevent
// layout shift; milestones with no date show nothing (the "Add date" prompt
// is only interactive anyway).
// paddingBlock and typography match BorderlessInput (paddingBlock="100",
// typography="body-100") so the row height stays stable across the boundary.
function MilestoneDateContentFallback({
  milestone,
  paddingInlineStart
}) {
  if (!milestone.dueDate) return null;
  return /*#__PURE__*/_jsx(Text, {
    typography: "body-100",
    paddingBlock: "100",
    paddingInlineStart: paddingInlineStart,
    children: I18n.moment.utc(milestone.dueDate).format('ll')
  });
}
function MilestoneDueDateCellFallback({
  milestone
}) {
  return /*#__PURE__*/_jsx(StyledTableCell, {
    style: {
      paddingInlineStart: '8px'
    },
    children: /*#__PURE__*/_jsx(MilestoneDateContentFallback, {
      milestone: milestone
    })
  });
}
function MilestoneRowCellsComponent({
  milestone,
  onUpdateTitle,
  onUpdateStatus,
  onUpdateDueDate,
  onDelete,
  ownerCandidates,
  onOwnerChange,
  ownerCandidatesLoading,
  renderAvatar,
  sellerGroupName,
  onToggleMilestoneSidebar,
  suggestion,
  onSuggestionResolved,
  onSuggestionError,
  readOnly
}) {
  const size = useMilestoneTableSize();
  const handleDelete = () => onDelete(milestone.milestoneId);
  const handleSuggestionResolved = decision => onSuggestionResolved === null || onSuggestionResolved === void 0 ? void 0 : onSuggestionResolved(milestone.milestoneId, decision);
  const ownerProps = {
    milestone,
    ownerCandidates,
    onOwnerChange,
    ownerCandidatesLoading,
    renderAvatar,
    sellerGroupName,
    readOnly
  };
  if (size === 'small') {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StyledTableCell, {
        style: {
          verticalAlign: 'top'
        },
        children: /*#__PURE__*/_jsx(MilestoneStatusCheckbox, {
          milestone: milestone,
          onUpdateStatus: onUpdateStatus,
          readOnly: readOnly
        })
      }), /*#__PURE__*/_jsx(StyledTableCell, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          children: [/*#__PURE__*/_jsx(MilestoneTitleContent, {
            milestone: milestone,
            onUpdateTitle: onUpdateTitle,
            onToggleMilestoneSidebar: onToggleMilestoneSidebar,
            suggestion: suggestion,
            readOnly: readOnly
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "500",
            children: [/*#__PURE__*/_jsx(Suspense, {
              fallback: /*#__PURE__*/_jsx(MilestoneDateContentFallback, {
                milestone: milestone,
                paddingInlineStart: "0"
              }),
              children: /*#__PURE__*/_jsx(LazyMilestoneDateContent, {
                milestone: milestone,
                onUpdateDueDate: onUpdateDueDate,
                paddingInlineStart: "0",
                readOnly: readOnly
              })
            }), /*#__PURE__*/_jsx(MilestoneOwnerContent, Object.assign({}, ownerProps, {
              showName: false
            }))]
          })]
        })
      }), /*#__PURE__*/_jsx(StyledTableCell, {
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "end",
          alignItems: "center",
          children: !readOnly && suggestion ? /*#__PURE__*/_jsx(MilestoneSuggestionActions, {
            suggestion: suggestion,
            onResolved: handleSuggestionResolved,
            onError: onSuggestionError
          }) : /*#__PURE__*/_jsx(TableHoverContent, {
            children: /*#__PURE__*/_jsx(MilestoneMenu, {
              milestone: milestone,
              onDelete: handleDelete,
              readOnly: readOnly
            })
          })
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MilestoneSubjectCell, {
      milestone: milestone,
      onUpdateTitle: onUpdateTitle,
      onUpdateStatus: onUpdateStatus,
      onToggleMilestoneSidebar: onToggleMilestoneSidebar,
      suggestion: suggestion,
      readOnly: readOnly
    }), /*#__PURE__*/_jsx(Suspense, {
      fallback: /*#__PURE__*/_jsx(MilestoneDueDateCellFallback, {
        milestone: milestone
      }),
      children: /*#__PURE__*/_jsx(LazyMilestoneDueDateCell, {
        milestone: milestone,
        onUpdateDueDate: onUpdateDueDate,
        readOnly: readOnly
      })
    }), /*#__PURE__*/_jsx(MilestoneOwnerCell, Object.assign({}, ownerProps, {
      showName: size === 'full'
    })), /*#__PURE__*/_jsx(StyledTableCell, {
      children: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "end",
        alignItems: "center",
        gap: "100",
        children: !readOnly && suggestion ? /*#__PURE__*/_jsx(MilestoneSuggestionActions, {
          suggestion: suggestion,
          onResolved: handleSuggestionResolved,
          onError: onSuggestionError
        }) : /*#__PURE__*/_jsx(TableHoverContent, {
          children: /*#__PURE__*/_jsx(MilestoneMenu, {
            milestone: milestone,
            onDelete: handleDelete,
            readOnly: readOnly
          })
        })
      })
    })]
  });
}
export const MilestoneRowCells = /*#__PURE__*/memo(MilestoneRowCellsComponent);